import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, ErrorMessage, Field, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import Switch from "components/Shared/Switch"
import { addIssueSchema } from "components/Common/validation"
import {
  addIssueType,
  getIssueTypeDetails,
  updateIssueTypeDetails,
} from "store/actions/customerSupportAction"
import { showAlert } from "store/reducers/alertReducer"
import { useSelector } from "react-redux"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  title: "",
  titleAr: "",
  isAttachmentRequired: false,
  displayOrder: 0,
  status: null,
}

const AddIssue = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Issue | Namq"
  )}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const [formValues, setFormValues] = useState(initialValues)

  const { loading, isLoading, isUpdating, issueTypeDetails } = useSelector(
    state => state.CustomerSupport
  )

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      17,
      "customer_support_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      17,
      "customer_support_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      fetchOldDetails()
    }
  }, [])

  const fetchOldDetails = () => {
    const payload = { id }
    dispatch(getIssueTypeDetails(payload))
  }

  useEffect(() => {
    if (issueTypeDetails && !loading && mode === "edit") {
      const { title, titleAr, isAttachmentRequired, displayOrder, status } =
        issueTypeDetails
      setFormValues({
        ...formValues,
        title,
        titleAr,
        isAttachmentRequired,
        displayOrder,
        status,
      })
    }
  }, [issueTypeDetails])

  const handleOnSubmit = values => {
    const { title, titleAr, isAttachmentRequired, displayOrder, status } =
      values
    const payload = {
      title,
      titleAr,
      displayOrder,
      isAttachmentRequired,
    }
    if (mode === "add") {
      dispatch(addIssueType(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/support-issues`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id,
        status: `${status}`,
      }
      dispatch(updateIssueTypeDetails(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/support-issues`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid add_coupon_section">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton /> {mode === "add" ? t("Add") : t("Edit")}{" "}
                  {t("Support Issue")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addIssueSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="title">
                              <span className="text-danger">*</span>{" "}
                              {t("Title(English)")}
                            </label>
                            <Field
                              type="text"
                              id="title"
                              name="title"
                              placeholder={t("Title(English)")}
                              className={`form-control ${
                                touched.title && errors.title
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="title" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="titleAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Title(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="titleAr"
                              name="titleAr"
                              placeholder={t("Title(عربي)")}
                              className={`form-control ${
                                touched.titleAr && errors.titleAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="titleAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="displayOrder">
                              <span className="text-danger">*</span>{" "}
                              {t("Display Order")}
                            </label>
                            <Field
                              type="text"
                              id="displayOrder"
                              name="displayOrder"
                              placeholder={t("Display Order")}
                              className={`form-control ${
                                touched.displayOrder && errors.displayOrder
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="displayOrder" />
                          </div>
                        </div>

                        {/* <div className="col-lg-3">
                          <div className="form_field">
                            <label htmlFor="status">Status</label>
                            <Switch
                              isChecked={values.status}
                              onClickHandler={() => {
                                setFieldValue("status", !values.status)
                              }}
                            />
                          </div>
                        </div> */}

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="isAttachmentRequired">
                              {t("Is Attachment Required")}
                            </label>
                            <Switch
                              isChecked={values.isAttachmentRequired}
                              onClickHandler={() => {
                                setFieldValue(
                                  "isAttachmentRequired",
                                  !values.isAttachmentRequired
                                )
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end ">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
export default AddIssue
